import React, { useState, useRef, useCallback } from "react";
import cn from "classnames";
import styles from "./Settings.module.sass";
import TooltipGlodal from "../../components/TooltipGlodal";
import Dropdown from "../../components/Dropdown";
import EmployerInformation from "./EmployerInformation";
import StudentInformation from "./StudentInformation";
import FormationInformation from "./FormationInformation";
import SalaryInformation from "./SalaryInformation";
import Conditions from "./Conditions";
import ContractInformation from "./ContractInformation";
import CentreInformation from "./CentreInformation";
import { toast } from "react-toastify";
import { generateDocuments } from "../../services/contract.service";
import { createFolder } from "../../services/folder.service";
import { createContract } from "../../services/contract.service";
import { useNavigate } from "react-router-dom";

const ContractCreate = () => {
  const navigate = useNavigate();
  const navigation = [
    {
      title: "Entreprise",
      action: () =>
        scrollToProfile.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Apprenti(e)",
      action: () =>
        scrollToLogin.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Formation",
      action: () =>
        scrollToFormation.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Contrat",
      action: () =>
        scrollToNotifications.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Salaire",
      action: () =>
        scrollToSalary.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Centre de formation",
      action: () =>
        scrollToPayment.current.scrollIntoView({ behavior: "smooth" }),
    },
    {
      title: "Récapitulatif & envoi",
      action: () =>
        scrollToPayment.current.scrollIntoView({ behavior: "smooth" }),
    },
  ];
  const options = [];
  navigation.map((x) => options.push(x.title));
  const [activeTab, setActiveTab] = useState(options[0]);

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToProfile = useRef(null);
  const scrollToLogin = useRef(null);
  const scrollToFormation = useRef(null);
  const scrollToNotifications = useRef(null);
  const scrollToSalary = useRef(null);
  const scrollToPayment = useRef(null);

  // États pour stocker les données du formulaire
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedTutor2, setSelectedTutor2] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [trainingDetails, setTrainingDetails] = useState({});
  const [contractDetails, setContractDetails] = useState({});
  const [salaryDetails, setSalaryDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Progression du dossier - État pour arriver à l'étape "Validation par l'école"
  const [progression, setProgression] = useState({
    "etudiant": {
      "infos_remplies": true,
      "sign_cerfa": false
    },
    "entreprise": {
      "infos_remplies": true,
      "contrat_cree": true,
      "tuteur_assigne": true,
      "remuneration_ok": true,
      "opco_assigne": true,
      "sign_cerfa": false,
      "sign_convention": false
    },
    "ecole": {
      "cerfa_genere": true,
      "valide_cerfa": false,
      "sign_cerfa": false,
      "sign_convention": false
    }
  });

  const handleClick = (x, index) => {
    setActiveIndex(index);
    x.action();
  };

  // Fonction pour gérer la sélection d'une entreprise
  const handleCompanySelect = useCallback((company) => {
    setSelectedCompany(company);
    setSelectedTutor(null); // Reset tuteur quand on change d'entreprise
  }, []);

  // Fonction pour gérer la sélection des tuteurs
  const handleTutorSelect = useCallback((tutor, isSecondTutor = false) => {
    if (isSecondTutor) {
      setSelectedTutor2(tutor);
    } else {
      setSelectedTutor(tutor);
    }
  }, []);

  // Fonction pour gérer la sélection d'un étudiant
  const handleStudentSelect = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  // Fonction pour gérer la sélection d'un centre de formation
  const handleCenterSelect = useCallback((center) => {
    setSelectedCenter(center);
  }, []);

  // Fonction pour gérer les détails du contrat
  const handleContractDetailsChange = useCallback((details) => {
    setContractDetails(details);
  }, []);

  // Fonction pour gérer les détails de formation
  const handleTrainingDetailsChange = useCallback((details) => {
    setTrainingDetails(details);
  }, []);

  // Fonction pour gérer les détails de salaire
  const handleSalaryDetailsChange = useCallback((details) => {
    setSalaryDetails(details);
  }, []);

  // Fonction pour gérer la sélection d'une formation
  const handleFormationSelect = useCallback((formationId) => {
    setSelectedFormation(formationId);
    setSelectedPromotion(null); // Reset promotion when formation changes
  }, []);

  // Fonction pour gérer la sélection d'une promotion
  const handlePromotionSelect = useCallback((promotionId) => {
    setSelectedPromotion(promotionId);
  }, []);

  // Fonction pour soumettre le formulaire et générer les documents
  const handleSubmit = async () => {

    if (!selectedCompany) {
      toast.error("Veuillez sélectionner une entreprise");
      return;
    }

    if (!selectedStudent) {
      toast.error("Veuillez sélectionner un(e) apprenti(e)");
      return;
    }

    if (!selectedCenter) {
      toast.error("Veuillez sélectionner un centre de formation");
      return;
    }

    if (!selectedTutor) {
      toast.error("Veuillez sélectionner un maître d'apprentissage");
      return;
    }

    if (!selectedFormation) {
      toast.error("Veuillez sélectionner une formation");
      return;
    }

    if (!selectedPromotion) {
      toast.error("Veuillez sélectionner une promotion");
      return;
    }

    setIsSubmitting(true);

    try {
      // Construire le tableau des tuteurs
      const tutorIds = [selectedTutor.id];
      if (selectedTutor2) {
        tutorIds.push(selectedTutor2.id);
      }

      // 1. Créer le contrat
      const contractData = {
        student_id: selectedStudent.id,
        contract_type: 11,
        status: "en_cours"
      };
      const createdContract = await createContract(contractData);
      const newContractId = createdContract.id;

      // 2. Créer le dossier avec toutes les informations
      const folderData = {
        student_id: selectedStudent.id,
        company_id: selectedCompany.id,
        center_id: selectedCenter.id,
        tutor_id: tutorIds,
        formation_id: selectedFormation,
        promotion_id: selectedPromotion,
        contract_id: newContractId,
        progression: progression,
        status: "en_cours",
        course_duration: trainingDetails.courseDuration || 0,
        number_of_hoursinstudy: trainingDetails.numberOfHoursInStudy || 0
      };
      const createdFolder = await createFolder(folderData);
      const newFolderId = createdFolder.id;

      // 3. Mettre à jour le contrat avec les détails finaux
      if (contractDetails && Object.keys(contractDetails).length > 0) {
        const { updateContract } = await import('../../services/contract.service');
        
        // Fonction helper pour convertir une date au format ISO si elle existe
        const formatDateToISO = (dateString) => {
          if (!dateString) return null;
          try {
            const date = new Date(dateString);
            return date.toISOString();
          } catch (error) {
            console.warn('Erreur de formatage de date:', dateString, error);
            return null;
          }
        };

        // Fonction helper pour convertir les coûts en nombre
        const parseNumericValue = (value) => {
          if (!value) return 0;
          // Supprimer les caractères non numériques (€, espaces, etc.)
          const numericString = value.toString().replace(/[^\d.,]/g, '');
          const parsed = parseFloat(numericString.replace(',', '.'));
          return isNaN(parsed) ? 0 : parsed;
        };

        await updateContract(newContractId, {
          // Relations
          student_id: selectedStudent.id,
          company_id: selectedCompany.id,
          center_id: selectedCenter.id,
          tutor_id: tutorIds,
          formation_id: selectedFormation,
          promotion_id: selectedPromotion,
          
          // Dates du contrat (mapping depuis contractDetails)
          start_date_of_contract: formatDateToISO(contractDetails.startDateOfContract),
          end_date_of_contract: formatDateToISO(contractDetails.endDateOfContract),
          training_start_date: formatDateToISO(contractDetails.trainingStartDate),
          training_end_date: formatDateToISO(contractDetails.trainingEndDate),
          
          // Travail dangereux
          is_dangerous_work: contractDetails.isDangerousWork || false,
          
          // Coûts et frais
          equipment_costs: parseNumericValue(contractDetails.firstEquipmentCost),
          mobility_costs: parseNumericValue(contractDetails.mobilityCost),
          food_costs: parseNumericValue(contractDetails.mealsCount) || 0,
          lodging_costs: parseNumericValue(contractDetails.nightsCount) || 0,
          
          // Informations de travail
          work_hours: contractDetails.workHours,
          work_minutes: contractDetails.workMinutes,
          
          // Types et modes contractuels
          contract_type: contractDetails.contractType,
          derogation_type: contractDetails.derogationType,
          mode_contractuel_type: contractDetails.modeContractuelType || "1",
          previous_contract_number: contractDetails.previousContractNumber || "",
          
          // Données de rémunération
          hiring_salary: salaryDetails.hiring_salary || '',
          has_convention_salary: salaryDetails.has_convention_salary || 'Non',
          convention_salary: salaryDetails.convention_salary || '',
          smic_percentage: salaryDetails.smic_percentage || '',
          annual_remuneration: JSON.stringify(salaryDetails.annual_remuneration || []),
          
          // Autres champs avec valeurs par défaut
          other_avantage: contractDetails.needsSocialSupport || false,
          actif: true
        });
      }

      // 4. Générer les documents avec l'ID du dossier
      const result = await generateDocuments(newFolderId);

      if (result && result.documents && result.documents.length > 0) {
        toast.success(`Dossier créé avec succès ! ${result.documents.length} documents générés.`);
      } else {
        toast.success("Dossier créé avec succès !");
      }

      // Rediriger vers la liste des dossiers au lieu de /contract/
      navigate("/admin/folders/");
    } catch (error) {
      console.error('Erreur lors de la finalisation du dossier:', error);
      toast.error("Erreur lors de la création du dossier");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.settings}>

          <div className={styles.menu}>
            {navigation.map((x, index) => (
              <button
                className={cn(styles.button, {
                  [styles.active]: activeIndex === index,
                })}
                key={index}
                onClick={() => handleClick(x, index)}
              >
                {x.title}
              </button>
            ))}
          </div>

          <div className={styles.wrapper}>
            <Dropdown
              className={styles.dropdown}
              classDropdownHead={styles.dropdownHead}
              value={activeTab}
              setValue={setActiveTab}
              options={options}
            />
            <div className={styles.list}>
              <div className={cn(styles.item, {[styles.active]: activeTab === options[0],})}>
                <div className={styles.anchor} ref={scrollToProfile}></div>
                <EmployerInformation onSelectCompany={handleCompanySelect} onSelectTutor={handleTutorSelect} />
                </div>


              <div className={cn(styles.item, {[styles.active]: activeTab === options[1],})}>
                <div className={styles.anchor} ref={scrollToLogin}></div>
                <StudentInformation onSelectStudent={handleStudentSelect} />
              </div>  

              <div className={cn(styles.item, {[styles.active]: activeTab === options[2],})}>
                <div className={styles.anchor} ref={scrollToFormation}></div>
                <FormationInformation 
                  onSelectFormation={handleFormationSelect} 
                  onSelectPromotion={handlePromotionSelect}
                  onTrainingDetailsChange={handleTrainingDetailsChange}
                />
              </div>
              
              <div className={cn(styles.item, {[styles.active]: activeTab === options[3],})}>
                <div className={styles.anchor} ref={scrollToNotifications}></div>
                <ContractInformation 
                  selectedStudent={selectedStudent} 
                  onDetailsChange={handleContractDetailsChange} 
                />
              </div>
              
              <div className={cn(styles.item, {[styles.active]: activeTab === options[4],})}>
                <div className={styles.anchor} ref={scrollToSalary}></div>
                <SalaryInformation 
                  onSalaryDetailsChange={handleSalaryDetailsChange}
                  selectedStudent={selectedStudent}
                  contractDetails={contractDetails}
                />
              </div>

              <div className={cn(styles.item, {[styles.active]: activeTab === options[5],})}>
                <div className={styles.anchor} ref={scrollToPayment}></div>
                <CentreInformation onSelectCenter={handleCenterSelect} />
              </div>


              <div className={cn(styles.item, {[styles.active]: activeTab === options[6],})}>
                <div className={styles.anchor} ref={scrollToPayment}></div>
                <Conditions/>
              </div>
            </div>   
            
            <button className={cn("button", styles.button)} onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Création en cours..." : "Créer le dossier"}
            </button>
          </div>
        </div>
      </div>
      <TooltipGlodal />
    </>
  );
};

export default ContractCreate;